/**
 * Générateur — Utiliser des échelles simples (plan, carte)
 * Attendu ID : 686 | Niveau : 6ème | Domaine : Proportionnalité (cluster 43)
 *
 * Types générés (1 catégorie, 6 formats) :
 *   - plan_vers_reel_saisie   : saisie — distance plan → distance réelle (m)
 *   - reel_vers_plan_saisie   : saisie — distance réelle → distance plan (cm)
 *   - interpretation_qcm      : QCM 4 choix — interpréter une notation d'échelle
 *   - echelle_proprietes_vf   : V/F 6 lignes — propriétés des échelles
 *   - choix_echelle_qcm       : QCM 4 choix — choisir l'échelle adaptée au contexte
 *   - conversion_saisie       : saisie — distance avec conversion d'unité (m / km)
 *
 * Spécificité : 100 % texte. Résultats garantis entiers via des tuples (échelle, d) pré-validés.
 *
 * Partition avec attendus voisins : 686 est un cas d'application de la proportionnalité
 * (aucun attendu voisin dans le cluster 43 ; 682-685 posent les bases).
 *
 * Cible : 20/20 uniques sur 20 itérations, 5 runs consécutifs minimum.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 686;

// Tuples (échelle n, distances plan d en cm) pour F1 plan→réel (résultat en m entier)
const F1_TUPLES = [
    // n=100 : 1 cm plan = 1 m réel
    { n: 100,  ds: [1, 2, 3, 4, 5, 8, 10] },
    // n=200 : 1 cm = 2 m
    { n: 200,  ds: [1, 2, 3, 4, 5, 8, 10] },
    // n=500 : 1 cm = 5 m
    { n: 500,  ds: [1, 2, 3, 4, 5, 8, 10] },
    // n=1000 : 1 cm = 10 m
    { n: 1000, ds: [1, 2, 3, 4, 5, 8, 10] },
    // n=2000 : 1 cm = 20 m
    { n: 2000, ds: [1, 2, 3, 4, 5, 8, 10] },
    // n=5000 : 1 cm = 50 m
    { n: 5000, ds: [1, 2, 3, 4, 5, 8] },
];

// Objets d'énoncé pour F1, avec leur plage vraisemblable en mètres (revue
// 2026-08-11) : l'objet est choisi APRÈS le calcul de la longueur réelle,
// parmi ceux dont la plage la contient — fini les « maisons de 4 m ».
const F1_OBJETS = [
    { phrase: 'la largeur d\'un couloir',           min: 1,  max: 3 },
    { phrase: 'la longueur d\'une pièce',           min: 2,  max: 15 },
    { phrase: 'la largeur d\'un salon',             min: 2,  max: 15 },
    { phrase: 'la longueur d\'une chambre',         min: 2,  max: 15 },
    { phrase: 'la longueur d\'une maison',          min: 6,  max: 40 },
    { phrase: 'la longueur d\'un jardin',           min: 5,  max: 100 },
    { phrase: 'la largeur d\'un terrain',           min: 5,  max: 100 },
    { phrase: 'la distance entre deux bâtiments',   min: 10, max: 500 },
    { phrase: 'la longueur d\'une rue',             min: 50, max: 2000 },
    { phrase: 'la longueur d\'un parc',             min: 50, max: 2000 },
];

// Tuples (échelle n, distances réelles D en m) pour F2 réel→plan (résultat en cm entier)
const F2_TUPLES = [
    // n=10 : D m → D×10 cm (D ≤ 3 : au-delà le plan dépasse 30 cm, revue 2026-08-11)
    { n: 10,  Ds: [1, 2, 3], unite: 'm' },
    // n=20 : D m → D×5 cm (idem : D=10 donnait un plan de 50 cm)
    { n: 20,  Ds: [1, 2, 3, 4, 5], unite: 'm' },
    // n=50 : D m → D×2 cm
    { n: 50,  Ds: [1, 2, 3, 5, 10], unite: 'm' },
    // n=100 : D m → D cm
    { n: 100, Ds: [1, 2, 3, 5, 10, 20], unite: 'm' },
    // n=200 : D m → D/2 cm (D pair)
    { n: 200, Ds: [2, 4, 6, 10, 20], unite: 'm' },
];

// Objets F2 avec leur plage vraisemblable en mètres (revue 2026-08-11) :
// l'objet est choisi APRÈS le tirage de la mesure réelle, parmi ceux dont
// la plage la contient — fini les « bâtiments de 2 m ».
const F2_OBJETS = [
    { objet: 'une armoire',        min: 1,  max: 3 },
    { objet: 'une table',          min: 1,  max: 3 },
    { objet: 'un lit',             min: 2,  max: 3 },
    { objet: 'un canapé',          min: 2,  max: 4 },
    { objet: 'une piscine',        min: 3,  max: 25 },
    { objet: 'une terrasse',       min: 3,  max: 15 },
    { objet: 'une salle',          min: 5,  max: 30 },
    { objet: 'un jardin',          min: 5,  max: 100 },
    { objet: 'un bâtiment',        min: 10, max: 100 },
    { objet: 'un gymnase',         min: 20, max: 50 },
    { objet: 'une cour d\'école',  min: 20, max: 100 },
    { objet: 'un terrain',         min: 10, max: 200 },
];

// Tuples pour F6 (carte / conversion d'unité)
const F6_TUPLES = [
    // n=1000 : d cm plan → d×10 m (d ≥ 5 : en dessous, « deux lieux » à
    // 10-30 m n'est pas crédible sur une carte — revue 2026-08-11)
    { n: 1000,   ds: [5, 8, 10], unite_cible: 'm' },
    // n=5000 : d cm plan → d×50 m
    { n: 5000,   ds: [1, 2, 4, 5, 8, 10], unite_cible: 'm' },
    // n=10000 : d cm plan → d×100 m ; pour km on prend d multiple de 10
    { n: 10000,  ds: [10, 20, 30],        unite_cible: 'km' },
    // n=25000 : d cm → d×250 m ; pour km, d multiple de 4
    { n: 25000,  ds: [4, 8, 12, 20],      unite_cible: 'km' },
    // n=50000 : d cm → d×500 m ; pour km, d pair
    { n: 50000,  ds: [2, 4, 6, 10, 20],   unite_cible: 'km' },
    // n=100000 : d cm → d×1 km
    { n: 100000, ds: [1, 2, 3, 5, 8, 10, 15, 20], unite_cible: 'km' },
];

// Choix d'échelle (F5) : contexte → (bonne échelle, distracteurs manifestement inadaptés)
const F5_CONTEXTS = [
    {
        enonce: 'une salle de classe (environ 10 m sur 8 m)',
        bonne:  '1/100',
        distracteurs: ['1/10000', '1/1000000', '1/1'],
    },
    {
        enonce: 'un meuble (environ 2 m)',
        bonne:  '1/20',
        distracteurs: ['1/10000', '1/100000', '1/5000'],
    },
    {
        enonce: 'une maison (environ 20 m sur 15 m)',
        bonne:  '1/200',
        distracteurs: ['1/2', '1/50000', '1/1000000'],
    },
    {
        enonce: 'un quartier (environ 500 m × 500 m)',
        bonne:  '1/5000',
        distracteurs: ['1/10', '1/100', '1/1000000'],
    },
    {
        enonce: 'une ville (environ 5 km de large)',
        bonne:  '1/50000',
        distracteurs: ['1/10', '1/100', '1/2'],
    },
    {
        enonce: 'une région (environ 100 km de large)',
        bonne:  '1/1000000',
        distracteurs: ['1/10', '1/100', '1/1000'],
    },
    {
        enonce: 'un jardin (environ 50 m)',
        bonne:  '1/500',
        distracteurs: ['1/5', '1/100000', '1/1000000'],
    },
    {
        enonce: 'un appartement (environ 80 m²)',
        bonne:  '1/100',
        distracteurs: ['1/2', '1/50000', '1/1000000'],
    },
    // Contextes ajoutés (revue 2026-08-11) : pool 8 -> 12, cibles vérifiées
    // au calcul (dessin entre 5 et 40 cm avec la bonne, jamais avec un leurre).
    {
        enonce: 'une salle de bain (environ 3 m)',
        bonne:  '1/50',
        distracteurs: ['1/5000', '1/100000', '1/1'],
    },
    {
        enonce: 'une cour de récréation (environ 60 m)',
        bonne:  '1/500',
        distracteurs: ['1/5', '1/50000', '1/1000000'],
    },
    {
        enonce: 'un collège (environ 150 m de long)',
        bonne:  '1/1000',
        distracteurs: ['1/10', '1/100000', '1/1000000'],
    },
    {
        enonce: 'un stade (environ 200 m de long)',
        bonne:  '1/2000',
        distracteurs: ['1/20', '1/100000', '1/2'],
    },
];

// Valeurs d'échelle utilisables pour F3 V2 (1 cm plan = ... réel)
const F3_V2_POOL = [
    { n: 100,    rep: '1 m',     distracteurs: ['10 m', '100 m', '10 cm'] },
    // « 200 cm » retiré (revue 2026-08-11) : il valait exactement 2 m, la bonne.
    { n: 200,    rep: '2 m',     distracteurs: ['20 m', '20 cm', '2 cm'] },
    { n: 500,    rep: '5 m',     distracteurs: ['50 m', '50 cm', '5 km'] },
    { n: 1000,   rep: '10 m',    distracteurs: ['100 m', '1 km', '1 m'] },
    { n: 10000,  rep: '100 m',   distracteurs: ['10 m', '1 km', '10 km'] },
    { n: 100000, rep: '1 km',    distracteurs: ['100 m', '10 km', '100 km'] },
];

const L_P2R   = 'Du plan à la réalité (saisie)';
const L_R2P   = 'De la réalité au plan (saisie)';
const L_INT   = 'Interpréter une échelle (QCM)';
const L_VF    = 'Propriétés des échelles (V/F)';
const L_CHOIX = 'Choisir l\'échelle adaptée (QCM)';
const L_CONV  = 'Distance avec conversion d\'unité (saisie)';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_P2R,
});


const fmt = (n) => {
    if (Number.isInteger(n)) return String(n);
    const s = Number(n).toFixed(2);
    const trimmed = s.replace(/\.?0+$/, '');
    return trimmed.replace('.', ',');
};

// ─── Format 1 — Plan → Réalité (Saisie) ──────────────────────

// Anti-répétition immédiate (revue 2026-08-11, recette standard).
let derniereSignatureP2R = null;

const genPlanVersReelSaisie = () => {
    let payload = construirePlanVersReelSaisie();
    for (let tries = 0; tries < 8 && payload.texte === derniereSignatureP2R; tries++) {
        payload = construirePlanVersReelSaisie();
    }
    derniereSignatureP2R = payload.texte;
    return payload;
};

const construirePlanVersReelSaisie = () => {
    const tuple = randomChoice(F1_TUPLES);
    const d = randomChoice(tuple.ds);

    const distanceReelleCm = d * tuple.n;
    const distanceReelleM = distanceReelleCm / 100;
    const reponse = fmt(distanceReelleM);

    const candidats = F1_OBJETS.filter(o => distanceReelleM >= o.min && distanceReelleM <= o.max);
    const objet = randomChoice(candidats).phrase;

    const texte = `<p>Sur un plan à l'échelle 1/${tuple.n}, ${objet} est représentée par une longueur de ${fmt(d)} cm. Quelle est sa longueur réelle, en mètres ?</p>`;

    const payload = makeSaisie(reponse, texte, {
        ...opts(L_P2R),
        strict: false,
        suffixe: ' m',
    });
    payload._meta.generatorKey = 'plan_vers_reel_saisie';
    return payload;
};

// ─── Format 2 — Réalité → Plan (Saisie) ──────────────────────

// Anti-répétition immédiate (revue 2026-08-11, recette standard).
let derniereSignatureR2P = null;

const genReelVersPlanSaisie = () => {
    let payload = construireReelVersPlanSaisie();
    for (let tries = 0; tries < 8 && payload.texte === derniereSignatureR2P; tries++) {
        payload = construireReelVersPlanSaisie();
    }
    derniereSignatureR2P = payload.texte;
    return payload;
};

const construireReelVersPlanSaisie = () => {
    const tuple = randomChoice(F2_TUPLES);
    const D = randomChoice(tuple.Ds);
    const candidats = F2_OBJETS.filter(o => D >= o.min && D <= o.max);
    const objet = randomChoice(candidats).objet;

    const D_cm = D * 100;
    const distancePlan = D_cm / tuple.n;
    const reponse = fmt(distancePlan);

    const masculin = objet.startsWith('un ');
    const capObjet = objet.charAt(0).toUpperCase() + objet.slice(1);
    const pronom = masculin ? 'il' : 'elle';
    const accord = masculin ? '' : 'e';
    const texte = `<p>${capObjet} mesure ${fmt(D)} m en réalité. Sur un plan à l'échelle 1/${tuple.n}, par combien de cm est-${pronom} représenté${accord} ?</p>`;

    const payload = makeSaisie(reponse, texte, {
        ...opts(L_R2P),
        strict: false,
        suffixe: ' cm',
    });
    // Calculatrice AUTORISÉE (décision utilisateur 2026-08-11) : la division
    // par l'échelle (ex. 600 ÷ 200) n'est pas évidente pour tous en 6e.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'reel_vers_plan_saisie';
    return payload;
};

// ─── Format 3 — Interprétation (QCM) ─────────────────────────

// Anti-répétition immédiate (revue 2026-08-11, recette standard) : deux
// variantes sont des textes fixes, l'élève retombait 2 fois de suite dessus.
let derniereSignatureInterp = null;

const genInterpretationQcm = () => {
    let payload = construireInterpretationQcm();
    for (let tries = 0; tries < 12 && payload.texte === derniereSignatureInterp; tries++) {
        payload = construireInterpretationQcm();
    }
    derniereSignatureInterp = payload.texte;
    return payload;
};

const construireInterpretationQcm = () => {
    const r = Math.random();
    let question, bonne, choix;

    if (r < 0.25) {
        // V1 — signification de 1/n
        const n = randomChoice([100, 200, 500, 1000, 5000, 10000]);
        question = `Que signifie l'échelle 1/${n} sur un plan ?`;
        bonne = `1 cm sur le plan représente ${n} cm en réalité.`;
        choix = [
            bonne,
            `1 m sur le plan représente ${n} cm en réalité.`,
            `${n} cm sur le plan représente 1 cm en réalité.`,
            `Le plan est ${n} fois plus grand que la réalité.`,
        ];
    } else if (r < 0.5) {
        // V2 — 1 cm plan → ?
        const item = randomChoice(F3_V2_POOL);
        question = `Sur un plan à l'échelle 1/${item.n}, une longueur de 1 cm sur le plan correspond à :`;
        bonne = item.rep;
        choix = [bonne, ...item.distracteurs];
    } else if (r < 0.75) {
        // V3 — nature d'une échelle
        question = `Sur un plan ou une carte, une échelle est :`;
        // Longueurs encadrées (revue 2026-08-11) : la bonne était strictement
        // la plus longue dans 100 % des tirages de cette variante.
        bonne = 'Un rapport entre la distance mesurée sur le plan et la distance réelle correspondante.';
        choix = [
            bonne,
            'La différence entre la distance mesurée sur le plan et la distance réelle correspondante.',
            'La somme de la distance mesurée sur le plan et de la distance réelle correspondante.',
            'Le produit de la distance sur le plan par la distance réelle.',
        ];
    } else {
        // V4 — ordre de grandeur
        question = `Sur un plan à l'échelle 1/n avec n supérieur à 1, les distances sur le plan sont :`;
        bonne = 'Plus petites que les distances réelles correspondantes.';
        choix = [
            bonne,
            'Plus grandes que les distances réelles correspondantes.',
            'Toujours égales aux distances réelles correspondantes.',
            'Sans rapport avec les distances réelles correspondantes.',
        ];
    }

    const texte = `<p>${question}</p>`;
    const payload = makeQcm(choix, bonne, texte, opts(L_INT));
    payload._meta.generatorKey = 'interpretation_qcm';
    return payload;
};

// ─── Format 4 — Propriétés (V/F) ─────────────────────────────

// Chaque item porte sa clé sémantique `k` (revue 2026-08-11) : une clé ne
// sort qu'une fois par tirage, camps Vrai et Faux confondus — sinon une
// affirmation donne l'autre par simple contradiction (85 % des tirages).
const BUILDERS_V_686 = [
    { k: 'proportionnalite', b: () => 'Une échelle 1/n est un cas particulier de proportionnalité entre les distances sur le plan et les distances réelles.' },
    { k: 'sens_operation',   b: () => 'Pour passer d\'une distance mesurée sur le plan à la distance réelle, on multiplie par n.' },
    { k: 'signification',    b: ({ n }) => `À l'échelle 1/${n}, une longueur de 1 cm sur le plan représente ${n} cm en réalité.` },
    { k: 'sens_operation',   b: () => 'Pour passer d\'une distance réelle à la distance correspondante sur le plan, on divise par n.' },
    { k: 'grandeur_n',       b: () => 'Plus n est grand dans une échelle 1/n, plus le plan représente une grande zone de la réalité.' },
    { k: 'grandeur_n',       b: () => 'Une carte d\'un pays est représentée à une échelle où n est très grand, par exemple 1/1 000 000.' },
    { k: 'notation',         b: () => 'Les notations « 1/n » et « 1 : n » désignent la même échelle.' },
    { k: 'proportionnalite', b: () => 'Le rapport entre la distance sur le plan et la distance réelle est le même, quelle que soit la paire de points considérée.' },
];

const BUILDERS_F_686 = [
    { k: 'reduction',        b: () => 'Sur un plan à l\'échelle 1/n avec n supérieur à 1, les distances sur le plan sont plus grandes que les distances réelles.' },
    { k: 'signification',    b: () => 'À l\'échelle 1/100, 1 cm sur le plan représente 100 m en réalité.' },
    { k: 'sens_operation',   b: ({ n }) => `À l'échelle 1/${n}, pour passer d'une distance sur le plan à la distance réelle, on divise par ${n}.` },
    { k: 'reduction',        b: () => 'Une échelle 1/1 signifie que le plan est plus petit que la réalité.' },
    { k: 'domaine',          b: () => 'Les échelles ne concernent que les cartes géographiques, pas les plans d\'objets.' },
    { k: 'valeur_echelle',   b: () => 'Une échelle peut être égale à un nombre négatif.' },
    { k: 'signification',    b: () => 'À l\'échelle 1/1000, une distance de 5 cm sur le plan correspond à 5 m en réalité.' },
    { k: 'proportionnalite', b: () => 'Sur un plan à l\'échelle 1/100, deux distances différentes en réalité peuvent être représentées par la même longueur sur le plan.' },
];

const genEchelleProprietesVF = () => {
    const texte = `<p>Pour chaque affirmation concernant les échelles, indique si elle est vraie ou fausse.</p>`;

    for (let attempt = 0; attempt < 10; attempt++) {
        const n = randomChoice([100, 200, 500, 1000, 5000]);
        const ctxV = { n };
        const ctxF = { n };

        const seenKeys = new Set();
        const pick = (pool, nb) => {
            const res = [];
            for (const item of shuffle([...pool])) {
                if (res.length === nb) break;
                if (seenKeys.has(item.k)) continue;
                seenKeys.add(item.k);
                res.push(item);
            }
            return res;
        };
        // Quota variable (revue 2026-08-11) : 3V/3F constant était comptable.
        const nbV = randInt(2, 4);
        const lignesV = pick(BUILDERS_V_686, nbV).map(item => item.b(ctxV));
        const lignesF = pick(BUILDERS_F_686, 6 - nbV).map(item => item.b(ctxF));

        const combined = shuffle([
            ...lignesV.map(l => ({ ligne: l, br: 0 })),
            ...lignesF.map(l => ({ ligne: l, br: 1 })),
        ]);

        const lignes = combined.map(x => x.ligne);
        const bonnesReponses = combined.map(x => x.br);

        if (new Set(lignes).size === lignes.length) {
            const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
            payload._meta.generatorKey = 'echelle_proprietes_vf';
            return payload;
        }
    }

    // Fallback
    const fallbackLignes = [
        'Une échelle 1/n est un cas particulier de proportionnalité entre les distances sur le plan et les distances réelles.',
        'Pour passer d\'une distance mesurée sur le plan à la distance réelle, on multiplie par n.',
        'Les notations « 1/n » et « 1 : n » désignent la même échelle.',
        'Sur un plan à l\'échelle 1/n avec n supérieur à 1, les distances sur le plan sont plus grandes que les distances réelles.',
        'Une échelle peut être égale à un nombre négatif.',
        'Les échelles ne concernent que les cartes géographiques, pas les plans d\'objets.',
    ];
    const fallbackBr = [0, 0, 0, 1, 1, 1];
    const order = shuffle([0, 1, 2, 3, 4, 5]);
    const lignes = order.map(i => fallbackLignes[i]);
    const bonnesReponses = order.map(i => fallbackBr[i]);
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
    payload._meta.generatorKey = 'echelle_proprietes_vf';
    return payload;
};

// ─── Format 5 — Choix d'échelle (QCM) ────────────────────────

// Anti-répétition immédiate (revue 2026-08-11, recette standard).
let derniereSignatureChoixEch = null;

const genChoixEchelleQcm = () => {
    let payload = construireChoixEchelleQcm();
    for (let tries = 0; tries < 12 && payload.texte === derniereSignatureChoixEch; tries++) {
        payload = construireChoixEchelleQcm();
    }
    derniereSignatureChoixEch = payload.texte;
    return payload;
};

const construireChoixEchelleQcm = () => {
    const ctx = randomChoice(F5_CONTEXTS);
    // Sélectionner 3 distracteurs distincts de la bonne
    const distracteurs = shuffle(ctx.distracteurs.filter(d => d !== ctx.bonne)).slice(0, 3);
    const choix = [ctx.bonne, ...distracteurs];

    const question = `Quelle échelle est la plus adaptée pour représenter ${ctx.enonce} sur une feuille de format ordinaire ?`;
    const texte = `<p>${question}</p>`;

    const payload = makeQcm(choix, ctx.bonne, texte, opts(L_CHOIX));
    payload._meta.generatorKey = 'choix_echelle_qcm';
    return payload;
};

// ─── Format 6 — Conversion d'unité (Saisie) ──────────────────

// Anti-répétition immédiate (revue 2026-08-11, recette standard).
let derniereSignatureConv = null;

const genConversionSaisie = () => {
    let payload = construireConversionSaisie();
    for (let tries = 0; tries < 8 && payload.texte === derniereSignatureConv; tries++) {
        payload = construireConversionSaisie();
    }
    derniereSignatureConv = payload.texte;
    return payload;
};

const construireConversionSaisie = () => {
    const tuple = randomChoice(F6_TUPLES);
    const d = randomChoice(tuple.ds);

    const distanceReelleCm = d * tuple.n;
    let reponse;
    if (tuple.unite_cible === 'km') {
        reponse = fmt(distanceReelleCm / 100000);
    } else {
        reponse = fmt(distanceReelleCm / 100);
    }

    const texte = `<p>Sur une carte à l'échelle 1/${tuple.n}, la distance entre deux lieux est de ${fmt(d)} cm. Quelle est la distance réelle entre ces deux lieux, en ${tuple.unite_cible} ?</p>`;

    const payload = makeSaisie(reponse, texte, {
        ...opts(L_CONV),
        strict: false,
        suffixe: ` ${tuple.unite_cible}`,
    });
    // Calculatrice AUTORISÉE (décision utilisateur 2026-08-11) : produits du
    // type 8 × 25 000 puis conversion ÷ 100 000, cohérent avec le format 2.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'conversion_saisie';
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    plan_vers_reel_saisie:   genPlanVersReelSaisie,
    reel_vers_plan_saisie:   genReelVersPlanSaisie,
    interpretation_qcm:      genInterpretationQcm,
    echelle_proprietes_vf:   genEchelleProprietesVF,
    choix_echelle_qcm:       genChoixEchelleQcm,
    conversion_saisie:       genConversionSaisie,
};

export const CATEGORIES = [
    {
        id: 'echelles',
        label: 'Échelles (plans, cartes)',
        formatsList: [
            { key: 'plan_vers_reel_saisie', label: L_P2R },
            { key: 'reel_vers_plan_saisie', label: L_R2P },
            { key: 'interpretation_qcm',    label: L_INT },
            { key: 'echelle_proprietes_vf', label: L_VF },
            { key: 'choix_echelle_qcm',     label: L_CHOIX },
            { key: 'conversion_saisie',     label: L_CONV },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    F1_TUPLES,
    F1_OBJETS,
    F2_TUPLES,
    F2_OBJETS,
    F6_TUPLES,
    F5_CONTEXTS,
    F3_V2_POOL,
    BUILDERS_V_686,
    BUILDERS_F_686,
    fmt,
    genPlanVersReelSaisie,
    genReelVersPlanSaisie,
    genInterpretationQcm,
    genEchelleProprietesVF,
    genChoixEchelleQcm,
    genConversionSaisie,
};
